import React from "react";
import { WEATHER_ICONS } from "Utils/globals";
import {
  computewc,
  computehi,
  formatDaylightChange,
  capitalize,
} from "Utils/utils";
import {
  getWarningById,
  getSunData,
  getLocationsByIds,
} from "Utils/DataHelper";
//import { warningTestData, sunUpDownData, locationData } from "Utils/testData";
import { WeatherByHoursAccordion } from "Atoms/WeatherByHoursAccordion";

const getUvColour = (uv) => {
  let color;
  switch (true) {
    case uv < 3:
      //1-2
      color = "#00A349";
      break;
    case uv >= 3 && uv < 6:
      //3-5
      color = "#F8AD00";
      break;
    case uv >= 6 && uv < 8:
      //6-7
      color = "#F37D17";
      break;

    case uv >= 8 && uv < 11:
      //8-10
      color = "#FFAE00";
      break;
    default:
      //11 or higher
      color = "#8663A0";
      break;
  }
  return color;
};

const getFeelsLike = ({ temp, humidity, windSpeed }) => {
  if (temp > 25) {
    return computehi(temp, humidity);
  }
  return computewc(temp, windSpeed);
};

// Add sunset and rise to the dataset
// Loop through days and hours of that day, to set sunrise and sunset on the hours
// that are equal to or earlier than the sunset/sunrise hours
const addSunsetAndSunrise = ({ dataMap }) => {
  for (let [key, day] of dataMap) {
    let foundSunrise = false;
    let foundSunset = false;
    const newHours = day.hours
      .reverse()
      .map((hour) => {
        if (!foundSunrise && hour.hour <= day.upHour) {
          hour.sunUpTime = day.sunUpTime;
          foundSunrise = true;
        }
        if (!foundSunset && hour.hour <= day.downHour) {
          hour.sunDownTime = day.sunDownTime;
          foundSunset = true;
        }
        return hour;
      })
      .reverse();
    day.hours = newHours;
    dataMap.set(key, day);
  }
  return dataMap;
};

const getSunUpDownMap = ({ sunData }) => {
  // Create a lookup map where the date (e.g. 4) is the key and the vaule consists of an object with sun-set and rise values
  const sunUpDownMap = new Map();
  sunData.forEach(
    ({
      date,
      localSunUp,
      localSunDown,
      localSunDownTime: sunDownTime,
      localSunUpTime: sunUpTime,
      daylightChangeSeconds: daylightChange,
    }) => {
      const upDate = Number(date.substr(8, 2));
      let upHour = null;
      let downHour = null;

      if (localSunUp) {
        const { hour } = partsFromIsoString(localSunUp);
        upHour = hour;
      }
      if (localSunDown) {
        const { hour } = partsFromIsoString(localSunDown);
        downHour = hour;
      }

      const sunData = {
        upHour,
        downHour,
        sunUpTime,
        sunDownTime,
        daylightChange,
      };
      sunUpDownMap.set(upDate, sunData);
    }
  );
  return sunUpDownMap;
};

const partsFromIsoString = (isoDate) => {
  const year = Number(isoDate.substr(0, 4));
  const month = Number(isoDate.substr(5, 2));
  const date = Number(isoDate.substr(8, 2));
  const hour = Number(isoDate.substr(11, 2));
  return { year, month, date, hour };
};

const getSymbolMap = ({ twelveHourSymbols }) => {
  // Create a lookup map where the date (e.g. 4) is the key and the is the symbol
  const symbolMap = new Map();
  twelveHourSymbols.forEach(({ symbol12, localTimeIso }) => {
    const { date } = partsFromIsoString(localTimeIso);
    symbolMap.set(date, symbol12);
  });
  return symbolMap;
};

const getWarningMap = ({ warningData }) => {
  // Create a lookup map of warnings where the key is the date,
  // and value consists of an object with each hour as a key.
  const warningMap = new Map();
  if (warningData.warnings.length > 0) {
    warningData.warnings.forEach((warning) => {
      const { date, hour } = partsFromIsoString(warning.localTimeIso);
      const mapItem = warningMap.get(date);
      if (mapItem) {
        mapItem[hour] = {
          icon: warning.icon,
          warningList: warning.warning_list,
        };
        warningMap.set(date, mapItem);
      } else {
        const item = {};
        item[hour] = { icon: warning.icon, warningList: warning.warning_list };
        warningMap.set(date, item);
      }
    });
  }
  return warningMap;
};

const roundIfNotNull = (val) => {
  return val !== null ? Math.round(val) : val;
};

// If precip is more than 0.5 or less than 0.05 return a rounded integer (eg 2 0r 0)
// else return a float with one decimal (eg. 0.4)
const roundPrecip = (precip) => {
  const rounded =
    precip >= 0.5 || precip <= 0.05
      ? Math.round(precip)
      : Math.round(precip * 10) / 10;
  const roundedWithComma = rounded.toString().replace(/\./g, ",");
  return roundedWithComma;
};

const getMappedData = ({ data, sunData, warningData }) => {
  const { timeserie, aggData, twelveHourSymbols } = data;
  const { hour: startHour } = partsFromIsoString(timeserie[0].localTimeIso);
  const allDaysData = new Map();
  const sunUpDownMap = getSunUpDownMap({ sunData });
  const warningMap = getWarningMap({ warningData });
  const symbolMap = getSymbolMap({ twelveHourSymbols });

  //Mapping the aggData to a an object with data for the day,
  aggData.forEach(
    ({ maxTemp, minTemp, uvRadiation, precipSum, localTimeIso }, index) => {
      const { year, month, date } = partsFromIsoString(localTimeIso);
      const currentTime = new Date(year, month - 1, date);
      const symbol = symbolMap.get(date);
      //null values and empty object is preventing errors if data should be missing
      const {
        upHour = null,
        downHour = null,
        sunUpTime = null,
        sunDownTime = null,
        daylightChange = null,
      } = sunUpDownMap.get(date) || {};

      const roundedDayPrecip = roundPrecip(precipSum);

      const longFullDate = capitalize(
        new Intl.DateTimeFormat("da", {
          weekday: "long",
          month: "short",
          day: "numeric",
        }).format(currentTime)
      );
      const shortDay = capitalize(
        new Intl.DateTimeFormat("da", {
          weekday: "short",
        }).format(currentTime)
      );
      const shortDateMonth = new Intl.DateTimeFormat("da", {
        month: "short",
        day: "numeric",
      }).format(currentTime);

      const warning =
        warningData[`day${index}`] !== "none"
          ? warningData[`day${index}`]
          : null;

      const dayObj = {
        warning,
        longFullDate,
        shortDay,
        shortDateMonth,
        maxTemp,
        minTemp,
        uv: {
          value: uvRadiation ? uvRadiation.toFixed(1) : "--",
          color: uvRadiation ? getUvColour(uvRadiation) : null,
        },
        precip: roundedDayPrecip <= 0.1 ? null : roundedDayPrecip + " mm",
        upHour,
        downHour,
        daylightChangeString: formatDaylightChange(daylightChange),
        sunUpTime,
        sunDownTime,
        iconWeather: WEATHER_ICONS[symbol],
        symbol: symbol ? symbol + "" : null,
      };

      if (index === 0) {
        dayObj.timeSpanText = `kl. ${startHour}-24`;
      }
      //Add the day to the  data map
      const newDay = { ...dayObj, hours: [] };
      allDaysData.set(date, newDay);
    }
  );

  //Loop the timeseries data to add the hourly data to correct day
  timeserie.forEach(
    ({
      localTimeIso,
      precip1,
      precip3,
      precip6,
      temp,
      symbol,
      windGust,
      windSpeed,
      humidity,
      windDegree,
    }) => {
      //When data is incomplete
      if (temp === null || symbol === null) {
        return;
      }
      const hour = Number(localTimeIso.substr(11, 2));
      const date = Number(localTimeIso.substr(8, 2));

      // 1H, 3H or 6H
      const precip = precip1 ?? precip3 ?? precip6;
      // const precip = 0.0432;
      const roundedHourPrecip = roundPrecip(precip);

      const warningsOfdayData = warningMap.get(date) || {};
      const hourData = {
        hour,
        warnings: warningsOfdayData[hour] || {},
        iconWeather: WEATHER_ICONS[symbol],
        symbol,
        temperature: roundIfNotNull(temp),
        feelsLike: getFeelsLike({
          temp,
          windSpeed,
          humidity,
        }),
        precip: roundedHourPrecip <= 0.1 ? null : roundedHourPrecip + " mm",
        windSpeed: roundIfNotNull(windSpeed),
        windGust: roundIfNotNull(windGust),
        windDegree,
        humidity: roundIfNotNull(humidity),
      };

      let extraData = {};
      const inAllDays = allDaysData.get(date);

      if (!inAllDays.humidity && hour >= 12 && temp !== null) {
        const additionalDayData = {
          humidity: roundIfNotNull(humidity),
          windSpeed: roundIfNotNull(windSpeed),
        };
        if (inAllDays.symbol === null) {
          additionalDayData.symbol = symbol + "";
        }
        extraData = additionalDayData;
      }
      inAllDays.hours.push(hourData);
      allDaysData.set(date, { ...inAllDays, ...extraData });
    }
  );

  //Set timespan on last item (e.g. "Kl. 0-2"):
  const lastDayValue = Array.from(allDaysData.values()).pop();
  const lastHourValue =
    lastDayValue.hours.length > 0
      ? lastDayValue.hours[lastDayValue.hours.length - 1].hour
      : 0;

  //If last day is missing the symbol it means that the 12 h symbol is missing AND data is missing for 12 o'clock and later.
  if (lastDayValue.symbol === null) {
    const lastKeyInMap = Array.from(allDaysData.keys()).pop();
    allDaysData.delete(lastKeyInMap);
  }

  const timeSpanText = `Kl. 0-${lastHourValue}`;
  lastDayValue.timeSpanText = timeSpanText;

  //Add sun data to corresponding days and hours
  const allDaysWithSunData = addSunsetAndSunrise({ dataMap: allDaysData });
  return { allDaysData: allDaysWithSunData };
};

const WeatherByHours = () => {
  const [weatherData, setWeatherData] = React.useState([]);

  React.useEffect(() => {
    const fetchData = async () => {
      //København
      //sessionStorage.setItem("gid", 2618425);
      //sessionStorage.setItem("gid", 2699838);
      var gid = sessionStorage.getItem("gid");
      //console.log(gid);
      if (gid < 100) {
        return;
      }

      if (gid > 100) {
        const data = await getLocationsByIds({ ids: gid });
        const sunData = await getSunData({ gid });
        const warningData = await getWarningById({ id: gid });
        const { allDaysData } = getMappedData({
          data,
          sunData,
          warningData,
        });
        //Converting Map into array
        setWeatherData(Array.from(allDaysData.values()));
      }
    };
    fetchData();
  }, []);

  return WeatherByHoursAccordion({ weatherData });
};
export default WeatherByHours;
